import {
  RtbCampaignWebService,
  RestfulCampaignWebService
} from 'ws/RtbCampaignWebService';
import { getDisplayPrice } from 'helper/CampaignPriceHelper';
import {
  RtbCampaign,
  VideoAdViewObjective,
  RtbCampaignBasic,
  RtbCampaignPlanType,
  RtbLegacyOptimize,
  AdType,
  VideoProgressTrackingCode,
  RtbCampaignListBasic,
  RTBCAMPAIGN_DEFAULT_AGE_MIN,
  RTBCAMPAIGN_DEFAULT_AGE_MAX,
  SpaceDevice,
  SpaceDeliverabilityData
} from './RtbCampaign';
import _ from 'lodash';
import LocalDateTimeUtil from 'utils/LocalDateTimeUtil';
import moment from 'moment';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import i18n from 'i18n';
import {
  getLimitationContentLabel,
  isTestMode,
  toServerStructure,
  transformLimitationData
} from 'utils/LimitationUtil';
import { isAdTypeSupportOutdoorTA } from 'utils/CampaignUtil';
import {
  DELIVERY_TARGET_LIMITATION_UNITS,
  DeliveryTargetUnit,
  DeliveryTargetGroup,
  DeliveryTargetParentOptions,
  groupDeliveryTargets,
  parentSelectedMunicipalities
} from 'components/LimitationTableCell/groupStoreLimitations';
import { stripDerivedAdministrativeAreaFromLimitations } from 'core/rtbCampaign/MovableOutdoorAreaDerivation';
import {
  ageMaxOptions,
  ageMinOptions,
  getPmax2RtbAgeGroupsByAgeRange
} from 'core/limitation/l2ObjectTAOptions';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { LegacyEstimateData } from './LegacyEstimateData';
import { L1Object, L1ObjectObjective } from 'core/l1Object/L1Object';
import { BidPriceFloor, OrderType } from 'core/order/Order';
import { Dayparts } from 'core/dayparts/Dayparts';
import { TrackingData } from './TrackingData';
import { ScheduleData } from './ScheduleData';
import {
  CampaignAdTypeSetting,
  ChannelGroupSetting
} from 'core/channelGroup/ChannelGroupSettings';
import { AdTypeOptionItem } from 'core/campaignDashboard/CampaignDashboard';
import { ChannelType, DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager,
  storeInfoExtraGetter
} from 'core/adRequestSource/AdRequestSourceManager';

export type LegacyEstimateViewModelData = LegacyEstimateData & {
  involvedLimitatinoTypes: string[];
};
export interface RtbCampaignManager {
  getCampaignsOfGroup(
    l1ObjectId: number | string
  ): Promise<RtbCampaignListBasic[]>;
  getCampaign(campaignId): Promise<RtbCampaign>;
  getNoCidCampaign(campaignId): Promise<RtbCampaign>;
  createCampaign(
    campaign: RtbCampaign,
    l1ObjectId: number | string
  ): Promise<void>;
  createPmpCampaignAndCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    pmpId: number,
    creative: object
  ): Promise<void>;
  createPmpCampaignAndBindCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    pmpId: number,
    creativeIds: (number | string)[]
  ): Promise<void>;
  createCampaignAndCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    creative: object
  ): Promise<void>;
  createCampaignAndBindCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    creativeIds: (number | string)[]
  ): Promise<void>;
  updateCampaign(
    campaign: RtbCampaign,
    l1ObjectId: number | string
  ): Promise<void>;
  splitCampaign(
    campaign: RtbCampaign,
    origCamaignId,
    l1ObjectId: number | string
  ): Promise<void>;
  updateCampaignState(
    campaignData: {
      l2ChannelId: number | string;
      isDraft: boolean;
    }[],
    state: 'activate' | 'deactivate'
  ): Promise<void>;
  deleteCampaigns(campaignIds: Array<number>): Promise<void>;
  getCampaignOptions(from?: string, to?: string): Promise<Array<SelectOptions>>;
  getOrderNumber(campaignId: string | number): Promise<string>;
  getLegacyEstimateData(
    campaign: RtbCampaignBasic,
    limitations: LimitationData,
    campaignBidPrice: {
      type: string;
      autoBidCap: BidPriceFloor;
      bidFloor: BidPriceFloor;
    }[]
  ): Promise<LegacyEstimateViewModelData | undefined>;
  updateCampaignBidWeight(
    campaignId: string | number,
    bidWeight: number
  ): Promise<void>;
  updateCampaignAdminBidPrice(
    campaignId: string | number,
    adminBidPrice?: number
  ): Promise<void>;
  getMinBudgetOfCampaign(
    campaign: RtbCampaignBasic,
    budgetMinimumPerDay: number
  ): number;
  getRtbCampaignsUsingDailyBudget(
    l1ObjectId: number | string
  ): Promise<RtbCampaignListBasic[]>;
  prepareCreateCampaignPayload(campaign: RtbCampaign);
  getLegacyOptimizeValue(
    pmax3OptimizationGoal: L2ObjectOptimizationGoal,
    priceModel: RtbCampaignPlanType
  ): RtbLegacyOptimize;
  showFrequencyControl(
    l1Object: L1Object,
    optimize: L2ObjectOptimizationGoal
  ): boolean;
  getAdTypeOptions(): Promise<SelectOptions[]>;
  getCampaignAdTypeOptions(): Promise<AdTypeOptionItem[]>;
  getOutdoorSpacesDetail(
    spaceIds: string[],
    testMode: boolean
  ): Promise<SpaceDevice[]>;
  getLimitationSummaryData(
    limitation: LimitationData,
    orderType: OrderType,
    agencySegmentName: string,
    hiddenLimitations?: string[],
    deliveryTargetParentOptions?: DeliveryTargetParentOptions
  ): any;
  checkOptimizeSameAsPriceModel(
    priceModel: RtbCampaignPlanType,
    optimize: L2ObjectOptimizationGoal
  ): boolean;
  approvePmp(
    campaign: RtbCampaign,
    pmpId: number,
    l1ObjectId: number
  ): Promise<void>;
  rejectPmp(pmpId: number): Promise<void>;
  isSupportVideoAdType(adType: string): boolean;
  updateTrackingData(
    campaignIds: number[],
    trackingData: TrackingData
  ): Promise<void>;
  batchAddLimitations(
    adType: string,
    adTypeId: number | undefined,
    campaignIds: number[],
    limitations: LimitationData
  ): Promise<void>;
  batchRemoveLimitations(
    adType: string,
    campaignIds: number[],
    limitations: LimitationData
  ): Promise<void>;
  batchRemoveLimitations(
    adType: string,
    campaignIds: number[],
    limitations: LimitationData
  ): Promise<void>;
  updateScheduleData(
    campaignIds: number[],
    scheduleData: ScheduleData
  ): Promise<void>;
  getAgeMinAgeMaxInfoFromLimitations(limitations: LimitationData): {
    ageMin: number | undefined;
    ageMax: number | undefined;
    ageOptValue: 'include' | 'preferred';
  };
  downloadFixOutdoorDeliveryTargets(
    campaign: RtbCampaign,
    channelGroupId: number,
    limitations: LimitationData,
    deliveryUnit?: DeliveryUnit
  );
  getSpaceDeliverabilityData(
    groupId: number,
    adType: string | undefined,
    adTypeId: number | null | undefined,
    isTesting: boolean,
    limitations: LimitationData,
    campaignId?: number | null
  ): Promise<SpaceDeliverabilityData>;
}

export class DefaultRtbCampaignManager implements RtbCampaignManager {
  webService: RtbCampaignWebService;

  constructor(
    webService: RtbCampaignWebService = new RestfulCampaignWebService(),
    private adRequestSourceManager: AdRequestSourceManager = new DefaultAdRequestSourceManager()
  ) {
    this.webService = webService;
  }

  async getCampaign(campaignId: number): Promise<RtbCampaign> {
    const campaign = await this.webService.getCampaign(campaignId);
    if (!campaign.basic.adTypeId) {
      return campaign;
    }
    const adTypeSetting = ChannelGroupSetting.adTypeSettings().get(
      campaign.basic.adTypeId.toString()
    );
    const channelGroupSetting = adTypeSetting
      ? ChannelGroupSetting.fromId(adTypeSetting.channelGroupId)
      : undefined;
    // MOVABLE_OUTDOOR: strip BE-derived administrativeArea projection so no
    // downstream UI (form / list / summary) treats it as user state
    // (RKB-2541 §9.3a).
    if (
      channelGroupSetting?.config.channelType === ChannelType.MOVABLE_OUTDOOR
    ) {
      campaign.limitations = stripDerivedAdministrativeAreaFromLimitations(
        campaign.limitations
      );
    }
    if (
      !isAdTypeSupportOutdoorTA(campaign.basic.adType, campaign.basic.adTypeId)
    ) {
      return campaign;
    }
    const hasDeviceLimitation = Object.values(campaign.limitations)
      .flat()
      .some(
        (limitation) =>
          limitation.type === 'customOutdoorDevice' && !limitation.isGroup
      );
    if (
      adTypeSetting &&
      channelGroupSetting?.config.deliveryUnit === DeliveryUnit.STORE &&
      hasDeviceLimitation
    ) {
      await this.convertDeviceLimitationToStore(
        campaign.limitations,
        adTypeSetting.channelGroupId
      );
    }
    return campaign;
  }

  private async convertDeviceLimitationToStore(
    limitations: LimitationData,
    channelGroupId: number
  ): Promise<void> {
    const adFormat = 'Outdoor';
    const spaceAttributes =
      await this.adRequestSourceManager.getOutdoorSpaceAttributes(
        channelGroupId
      );
    const spaces = await this.adRequestSourceManager.getSpacesWithConditions(
      false,
      {},
      adFormat,
      channelGroupId,
      storeInfoExtraGetter
    );
    // 建立 spaceValue -> storeNum 的反查表
    const spaceValueToStoreNum = new Map<string | number, string>();
    for (const space of spaces) {
      if (space.extra) {
        spaceValueToStoreNum.set(space.value, space.extra);
      }
    }
    const storeNumOptions = spaceAttributes['storeNum'] || [];
    // 將 customOutdoorDevice limitation 轉換為 storeNum limitation
    for (const op of Object.keys(limitations) as Array<
      keyof typeof limitations
    >) {
      limitations[op] = limitations[op].map((limitation) => {
        if (limitation.type !== 'customOutdoorDevice' || limitation.isGroup) {
          return limitation;
        }
        const spaceValues = limitation.value as SelectOptions[];
        const storeNums = new Set(
          spaceValues
            .map((sv) => spaceValueToStoreNum.get(sv.value))
            .filter((sn): sn is string => sn !== undefined)
        );
        const allChildOptions = storeNumOptions.flatMap(
          (opt) => (opt as any).options || []
        );
        const finalStoreNumOptions = Array.from(storeNums).map((sn) => {
          const found = allChildOptions.find((opt) => String(opt.value) === sn);
          return found || { label: sn, value: sn };
        });
        return {
          ...limitation,
          type: 'storeNum',
          value: finalStoreNumOptions
        };
      });
    }
  }

  async getNoCidCampaign(campaignId: number): Promise<RtbCampaign> {
    const campaign = await this.getCampaign(campaignId);
    _.unset(campaign, 'basic.id');
    // RKB-2716: admin 調整過的價格只屬於來源活動。不清掉的話複製／切割出來的新活動
    // 會繼承別人的覆寫價，出價區塊還會因此被鎖成唯讀，而且它也會一路送回後端。
    // 這裡動的是 getCampaign() 當次新建的物件（每次都重新 wrap，沒有快取），
    // 不會影響其他地方持有的資料。
    _.unset(campaign, 'basic.adminBidPrice');
    _.set(
      campaign,
      'basic.name',
      campaign.basic.name + '_' + LocalDateTimeUtil.getYMDHMS()
    );
    return campaign;
  }

  async getCampaignsOfGroup(
    l1ObjectId: number | string
  ): Promise<RtbCampaignListBasic[]> {
    const campaigns = await this.webService.getCampaignsOfGroup(l1ObjectId);
    // MOVABLE_OUTDOOR: strip BE-derived administrativeArea projection from
    // each campaign in the list (same reason as getCampaign — §9.3a).
    return campaigns.map((campaign) => {
      const adTypeSetting = campaign.adTypeId
        ? ChannelGroupSetting.adTypeSettings().get(campaign.adTypeId.toString())
        : undefined;
      const channelGroupSetting = adTypeSetting
        ? ChannelGroupSetting.fromId(adTypeSetting.channelGroupId)
        : undefined;
      if (
        channelGroupSetting?.config.channelType !== ChannelType.MOVABLE_OUTDOOR
      ) {
        return campaign;
      }
      const limitations = campaign.additionalInfo?.limitations;
      if (!limitations) return campaign;
      return {
        ...campaign,
        additionalInfo: {
          ...campaign.additionalInfo,
          limitations:
            stripDerivedAdministrativeAreaFromLimitations(limitations)
        }
      };
    });
  }

  async createCampaign(
    campaign: RtbCampaign,
    l1ObjectId: number | string
  ): Promise<void> {
    const payload = this.prepareCreateCampaignPayload(campaign);
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    await this.webService.createCampaign(payload, l1ObjectId);
  }

  private prepareCreatePmpCampaignPayload(
    campaign: RtbCampaign,
    pmpId: number,
    l1ObjectId: number | string
  ): any {
    const campaignPayload = this.prepareCreateCampaignPayload(campaign);
    _.set(campaignPayload, 'campaign.l1ObjectId', l1ObjectId);

    const dealIdTA = _.defaultTo(campaign.limitations.other, []).find(
      (limitation) => limitation.type === 'dealId'
    );
    const spaceTA = campaign.limitations.include.find(
      (limitation) => limitation.type === 'adspace'
    );
    return {
      ...campaignPayload,
      campaign: _.omit(
        {
          ...campaignPayload.campaign,
          dealIds: dealIdTA
            ? (dealIdTA.value as SelectOptions[]).map((limit) => limit.value)
            : [],
          pmpId,
          ageMin: RTBCAMPAIGN_DEFAULT_AGE_MIN,
          ageMax: RTBCAMPAIGN_DEFAULT_AGE_MAX,
          isPmp: true
        },
        'adType'
      ),
      limitations: spaceTA ? transformLimitationData(false, 'inc', spaceTA) : []
    };
  }

  async createPmpCampaignAndCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    pmpId: number,
    creative: object
  ): Promise<void> {
    const payload = this.prepareCreatePmpCampaignPayload(
      campaign,
      pmpId,
      l1ObjectId
    );
    payload.creatives = [creative];
    await this.webService.createCampaignAndCreative(payload, l1ObjectId);
  }

  async createPmpCampaignAndBindCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    pmpId: number,
    creativeIds: (number | string)[]
  ): Promise<void> {
    const payload = this.prepareCreatePmpCampaignPayload(
      campaign,
      pmpId,
      l1ObjectId
    );
    payload.creativeIds = creativeIds;
    await this.webService.createCampaignAndBindCreative(payload, l1ObjectId);
  }

  async createCampaignAndCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    creative: object
  ): Promise<void> {
    const payload = this.prepareCreateCampaignPayload(campaign);
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    payload.creatives = [creative];
    await this.webService.createCampaignAndCreative(payload, l1ObjectId);
  }

  async createCampaignAndBindCreative(
    campaign: RtbCampaign,
    l1ObjectId: number | string,
    creativeIds: (number | string)[]
  ): Promise<void> {
    const payload = this.prepareCreateCampaignPayload(campaign);
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    payload.creativeIds = creativeIds;
    await this.webService.createCampaignAndBindCreative(payload, l1ObjectId);
  }

  async updateCampaign(
    campaign: RtbCampaign,
    l1ObjectId: number | string
  ): Promise<void> {
    const payload = this.wrapCampaignForServer(_.cloneDeep(campaign));
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    await this.webService.updateCampaign(payload, l1ObjectId);
  }

  async splitCampaign(
    campaign: RtbCampaign,
    origCamaignId,
    l1ObjectId: number | string
  ): Promise<void> {
    const payload = this.prepareCreateCampaignPayload(campaign);
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    await this.webService.splitCampaign(payload, origCamaignId, l1ObjectId);
  }

  async updateCampaignState(
    campaignData: {
      l2ChannelId: number | string;
      isDraft: boolean;
    }[],
    state: 'activate' | 'deactivate'
  ): Promise<void> {
    await this.webService.updateCampaignState(campaignData, state);
  }

  async deleteCampaigns(campaignIds: Array<number>): Promise<void> {
    if (campaignIds.length === 0) {
      return;
    }
    return this.webService.deleteCampaigns(campaignIds);
  }

  async getCampaignOptions(
    from?: string,
    to?: string
  ): Promise<Array<SelectOptions>> {
    return this.webService.getCampaignOptions(from, to);
  }

  async getOrderNumber(campaignId: string | number): Promise<string> {
    return this.webService.getOrderNumber(campaignId);
  }

  isArray = (
    value: (SelectOptions & { isGroup?: boolean })[] | string | number
  ): value is (SelectOptions & { isGroup?: boolean })[] => {
    return Array.isArray(value);
  };

  async getLegacyEstimateData(
    campaign: RtbCampaignBasic,
    limitations: LimitationData,
    campaignBidPrice: {
      type: string;
      autoBidCap: BidPriceFloor;
      bidFloor: BidPriceFloor;
    }[]
  ): Promise<LegacyEstimateViewModelData | undefined> {
    const optimize: L2ObjectOptimizationGoal = campaign.optimize;
    const priceModel: RtbCampaignPlanType = campaign.priceModel;
    const legacyOptimize: RtbLegacyOptimize = this.getLegacyOptimizeValue(
      optimize,
      priceModel
    );
    // RKB-2716: 預估數字要跟畫面顯示的價格同一個基準，否則會出現畫面顯示 266、
    // 預估卻是用 280 算出來的。
    let bidPrice = getDisplayPrice(campaign.adminBidPrice, campaign.bidPrice);
    const systemDefaultBidPrice = campaignBidPrice.find(
      (setting) => setting.type === campaign.adType
    );
    if (!bidPrice && systemDefaultBidPrice) {
      bidPrice = systemDefaultBidPrice.autoBidCap[_.camelCase(legacyOptimize)];
    }
    bidPrice = _.defaultTo(bidPrice, 0);
    const orderPrice = campaign.orderPrice;
    const includeLimitations = _.defaultTo(limitations.include, []);
    const excludeLimitations = _.defaultTo(limitations.exclude, []);
    const otherLimitations = _.defaultTo(limitations.other, []);
    const [instlLimitation] = _.remove(
      otherLimitations,
      (limitation: Limitation) => limitation.type === 'instl'
    );
    const involvedLimitatinoTypes: string[] = _.uniq(
      includeLimitations
        .concat(excludeLimitations)
        .concat(otherLimitations)
        .filter(
          (limitation) =>
            this.isArray(limitation.value) &&
            _.defaultTo(limitation.value, []).length > 0
        )
        .flatMap((limitation) => limitation.type)
    );

    !instlLimitation && involvedLimitatinoTypes.push('instl');
    const isLimitationSettingInvalid = involvedLimitatinoTypes.reduce(
      (acc: boolean, type: string) => {
        const hasInvalidLimitaiton = [
          'segment',
          'tenmaxAudience',
          'unicornlanguage'
        ].includes(type);
        return acc || hasInvalidLimitaiton;
      },
      false
    );

    if (
      // campaign.basic.priceModel === RtbCampaignPlanType.RB ||
      isLimitationSettingInvalid
    ) {
      return;
    }

    const includeLimitation = _.defaultTo(limitations.include, []);
    const preferredLimitation = _.defaultTo(limitations.preferred, []);
    const includeAgeMin = includeLimitation.find(
      (limitation) => limitation.type === 'age_min'
    );
    const ageMinLimitation =
      includeAgeMin ||
      preferredLimitation.find((limitation) => limitation.type === 'age_min');
    const ageMin = _.get(ageMinLimitation, 'value');
    const includeAgeMax = includeLimitation.find(
      (limitation) => limitation.type === 'age_max'
    );
    const ageMaxLimitation =
      includeAgeMax ||
      preferredLimitation.find((limitation) => limitation.type === 'age_max');
    const ageMax = _.get(ageMaxLimitation, 'value');

    const ageOptValue = includeAgeMin ? 'include' : 'preferred';

    const limitationUseAliasName = isAdTypeSupportOutdoorTA(
      campaign.adType,
      campaign.adTypeId
    );
    const limitation =
      ageOptValue === 'include'
        ? toServerStructure(
            {
              ...limitations,
              include: [
                ...limitations.include,
                {
                  op: 'inc',
                  type: 'age',
                  value: getPmax2RtbAgeGroupsByAgeRange(ageMin, ageMax)
                }
              ]
            },
            limitationUseAliasName
          )
        : toServerStructure(
            {
              ...limitations,
              preferred: [
                ...limitations.preferred,
                {
                  op: 'Preferred',
                  type: 'age',
                  value: getPmax2RtbAgeGroupsByAgeRange(ageMin, ageMax)
                }
              ]
            },
            limitationUseAliasName
          );
    const dealIdLimitation = _.defaultTo(
      otherLimitations.find((limitation) => limitation.type === 'dealId'),
      { value: [] }
    );
    if (
      this.isArray(dealIdLimitation.value) &&
      dealIdLimitation.value.length > 0
    ) {
      limitation.push({
        op: 'inc',
        limits: [...dealIdLimitation.value],
        isGroup: false,
        type: 'dealIds'
      });
    }
    const autoBidCap = _.get(
      campaignBidPrice.find(
        (bidPriceData) => bidPriceData.type === campaign.adType
      ),
      `autoBidCap`,
      {}
    );
    const autoBidCapPrice = _.defaultTo(
      autoBidCap[legacyOptimize.toLowerCase()],
      100
    );
    const dayPart: Omit<Dayparts, 'enabled'> | undefined = _.isEmpty(
      campaign.dayPart
    )
      ? undefined
      : _.omit(campaign.dayPart, ['enabled']);

    const apiPayload = {
      dayPart,
      orderId: campaign.orderId,
      campaignId: campaign.id,
      bidPrice: bidPrice
        ? parseFloat(bidPrice.toString())
        : orderPrice
          ? Math.min(parseFloat(orderPrice.toString()), autoBidCapPrice)
          : parseFloat(_.defaultTo(orderPrice, 0).toString()),
      optimize: legacyOptimize,
      limitation
    };
    const legacyEstimateData =
      await this.webService.getLegacyEstimateData(apiPayload);
    return { ...legacyEstimateData, involvedLimitatinoTypes };
  }

  async updateCampaignBidWeight(
    campaignId: string | number,
    bidWeight: number
  ): Promise<void> {
    return this.webService.updateCampaignBidWeight(campaignId, bidWeight);
  }

  async updateCampaignAdminBidPrice(
    campaignId: string | number,
    adminBidPrice?: number
  ): Promise<void> {
    return this.webService.updateCampaignAdminBidPrice(
      campaignId,
      adminBidPrice
    );
  }

  async getRtbCampaignsUsingDailyBudget(
    l1ObjectId: string | number
  ): Promise<RtbCampaignListBasic[]> {
    return this.webService.getRtbCampaignsUsingDailyBudget(l1ObjectId);
  }

  async getAdTypeOptions(): Promise<SelectOptions[]> {
    return this.webService.getAdTypeOptions();
  }

  async getCampaignAdTypeOptions(): Promise<AdTypeOptionItem[]> {
    return this.webService.getCampaignAdTypeOptions();
  }

  async getOutdoorSpacesDetail(
    spaceIds: string[],
    testMode: boolean
  ): Promise<SpaceDevice[]> {
    const requestSpaceIds = spaceIds.map((space) =>
      space.replaceAll('asiamax\\', '')
    );
    return this.webService.getOutdoorSpacesDetail(requestSpaceIds, testMode);
  }

  async approvePmp(
    campaign: RtbCampaign,
    pmpId: number,
    l1ObjectId: number
  ): Promise<void> {
    const payload = this.prepareCreatePmpCampaignPayload(
      _.cloneDeep(campaign),
      pmpId,
      l1ObjectId
    );
    return this.webService.approvePmp(payload, pmpId);
  }

  async rejectPmp(pmpId: number): Promise<void> {
    return this.webService.rejectPmp(pmpId);
  }

  async updateTrackingData(
    campaignIds: number[],
    trackingData: TrackingData
  ): Promise<void> {
    return this.webService.updateTrackingData(campaignIds, trackingData);
  }

  async batchAddLimitations(
    adType: string,
    adTypeId: number | undefined,
    campaignIds: number[],
    limitations: LimitationData
  ): Promise<void> {
    const { ageMin, ageMax, ageOptValue } =
      this.getAgeMinAgeMaxInfoFromLimitations(limitations);
    const serverLimitations = this.wrapLimitationForServer(
      adType,
      adTypeId,
      ageMin,
      ageMax,
      ageOptValue,
      limitations
    );

    return this.webService.batchAddLimitations(
      campaignIds,
      ageMin || RTBCAMPAIGN_DEFAULT_AGE_MIN,
      ageMax || RTBCAMPAIGN_DEFAULT_AGE_MAX,
      serverLimitations
    );
  }

  async batchRemoveLimitations(
    adType: string,
    campaignIds: number[],
    limitations: LimitationData
  ): Promise<void> {
    const limitationUseAliasName = isAdTypeSupportOutdoorTA(adType);
    const serverLimitations = toServerStructure(
      limitations,
      limitationUseAliasName
    );
    return this.webService.batchRemoveLimitations(
      campaignIds,
      serverLimitations
    );
  }

  async updateScheduleData(
    campaignIds: number[],
    scheduleData: ScheduleData
  ): Promise<void> {
    const payload: Partial<ScheduleData> = _.omitBy(
      {
        startDate: scheduleData.enableStartDate
          ? scheduleData.startDate
          : undefined,
        endDate: scheduleData.enableEndDate ? scheduleData.endDate : undefined
      },
      _.isNil
    );
    return this.webService.updateScheduleData(campaignIds, payload);
  }

  async downloadFixOutdoorDeliveryTargets(
    campaign: RtbCampaign,
    channelGroupId: number,
    limitations: LimitationData,
    deliveryUnit?: DeliveryUnit
  ) {
    const { id, name } = campaign.basic;
    return this.webService.downloadFixOutdoorDeliveryTargets(
      id,
      name,
      channelGroupId,
      this.toFixOutdoorServerLimitations(campaign, limitations),
      deliveryUnit
    );
  }

  private toFixOutdoorServerLimitations(
    campaign: RtbCampaign,
    limitations: LimitationData
  ) {
    const { adType, adTypeId } = campaign.basic;
    const serverLimitations = toServerStructure(
      limitations,
      isAdTypeSupportOutdoorTA(adType, adTypeId),
      {
        other: ['dealId'],
        preferred: ['age_min', 'age_max'],
        include: ['age_min', 'age_max', 'adx']
      }
    );
    return serverLimitations.map((limitation) => ({
      ...limitation,
      limits: limitation.limits.map((limit) => ({
        ...limit,
        value: String(limit.value).startsWith('asiamax\\')
          ? String(limit.value).replaceAll('asiamax\\', '')
          : limit.value
      }))
    }));
  }

  convertVideoAdEventToEvent(campaign: RtbCampaign) {
    const videoAdEvent = _.get(
      campaign,
      'basic.videoAdViewObjective.videoAdEvent'
    );
    const offset = _.get(campaign, 'basic.videoAdViewObjective.offset');

    const videoAdMetricEvent = _.get(
      campaign,
      'basic.videoAdViewObjective.videoAdMetricEvent'
    );
    _.unset(campaign, 'basic.videoAdViewObjective');
    switch (videoAdEvent) {
      case VideoAdViewObjective.METRIC:
        return _.set(
          campaign,
          'basic.videoAdViewObjective.event',
          videoAdMetricEvent
        );
      case VideoAdViewObjective.PROGRESS:
        _.set(campaign, 'basic.videoAdViewObjective.offset', offset);
        return _.set(
          campaign,
          'basic.videoAdViewObjective.event',
          videoAdEvent
        );
      default:
        return campaign;
    }
  }

  getMinBudgetOfCampaign(
    campaign: {
      startDate: string;
      endDate: string;
    },
    budgetMinimumPerDay: number
  ) {
    const startDate = moment(campaign.startDate);
    const endDate = moment(campaign.endDate);
    const scheduleDateCount = endDate.diff(startDate, 'days') + 1;
    return budgetMinimumPerDay * scheduleDateCount;
  }

  getLegacyOptimizeValue(
    pmax3OptimizationGoal: L2ObjectOptimizationGoal,
    priceModel: RtbCampaignPlanType
  ) {
    const legacyOptimizeMap = {
      [L2ObjectOptimizationGoal.REACH]: RtbLegacyOptimize.CPM,
      [L2ObjectOptimizationGoal.VIDEO_VIEWS]:
        priceModel === RtbCampaignPlanType.FVCPM
          ? RtbLegacyOptimize.VCPM
          : RtbLegacyOptimize.CPV,
      [L2ObjectOptimizationGoal.VIDEO_VIEWS_REACH]:
        priceModel === RtbCampaignPlanType.FVCPM
          ? RtbLegacyOptimize.VCPM
          : RtbLegacyOptimize.CPV,
      [L2ObjectOptimizationGoal.IMPRESSIONS]: RtbLegacyOptimize.CPM,
      [L2ObjectOptimizationGoal.CLICKS]: RtbLegacyOptimize.CPC,
      [L2ObjectOptimizationGoal.SALES]: RtbLegacyOptimize.CPC
    };
    return legacyOptimizeMap[pmax3OptimizationGoal];
  }

  getAgeMinAgeMaxInfoFromLimitations(limitations: LimitationData): {
    ageMin: number | undefined;
    ageMax: number | undefined;
    ageOptValue: 'include' | 'preferred';
  } {
    const ageOptValue =
      limitations.include &&
      limitations.include.find((limitation) => limitation.type === 'age_min')
        ? 'include'
        : 'preferred';
    const ageMinLimitation =
      ageOptValue === 'include'
        ? limitations.include.find(
            (limitation) => limitation.type === 'age_min'
          )
        : limitations.preferred
          ? limitations.preferred.find(
              (limitation) => limitation.type === 'age_min'
            )
          : undefined;
    const ageMin = ageMinLimitation
      ? (ageMinLimitation.value as number)
      : undefined;
    const ageMaxLimitation =
      ageOptValue === 'include'
        ? limitations.include.find(
            (limitation) => limitation.type === 'age_max'
          )
        : limitations.preferred
          ? limitations.preferred.find(
              (limitation) => limitation.type === 'age_max'
            )
          : undefined;
    const ageMax = ageMaxLimitation
      ? (ageMaxLimitation.value as number)
      : undefined;
    return { ageOptValue, ageMin, ageMax };
  }

  wrapLimitationForServer(
    adType: string,
    adTypeId: number | undefined,
    ageMin: number | undefined,
    ageMax: number | undefined,
    ageOptValue: 'include' | 'preferred',
    limitations: LimitationData
  ) {
    const limitationUseAliasName = isAdTypeSupportOutdoorTA(adType, adTypeId);
    return ageOptValue === 'include'
      ? toServerStructure(
          {
            ...limitations,
            include: [
              ...limitations.include,
              {
                op: 'inc',
                type: 'age',
                value: getPmax2RtbAgeGroupsByAgeRange(ageMin, ageMax)
              }
            ]
          },
          limitationUseAliasName
        )
      : toServerStructure(
          {
            ...limitations,
            preferred: [
              ...limitations.preferred,
              {
                op: 'Preferred',
                type: 'age',
                value: getPmax2RtbAgeGroupsByAgeRange(ageMin, ageMax)
              }
            ]
          },
          limitationUseAliasName
        );
  }

  wrapCampaignForServer = (campaign: RtbCampaign) => {
    let videoProgressTrackingCode:
      | (VideoProgressTrackingCode & { event: 'progress' })[]
      | undefined = undefined;
    if (
      campaign.basic.videoProgressTrackingCode &&
      campaign.basic.videoProgressTrackingCode.code &&
      campaign.basic.videoProgressTrackingCode.offset
    ) {
      videoProgressTrackingCode = [
        {
          ...campaign.basic.videoProgressTrackingCode,
          event: 'progress'
        }
      ];
    }

    const { ageMin, ageMax, ageOptValue } =
      this.getAgeMinAgeMaxInfoFromLimitations(campaign.limitations);

    const omitBasicProps = ['id', 'enableMonitor', 'tags', 'orderPriceEnable'];
    let adTypeSetting: CampaignAdTypeSetting | undefined;
    if (campaign.basic.adTypeId !== undefined) {
      omitBasicProps.push('adType');
      adTypeSetting = ChannelGroupSetting.adTypeSettings().get(
        campaign.basic.adTypeId.toString()
      );
    } else {
      if (campaign.basic.isPmp) {
        // PMP 活動的 adType 是前端合成的 'PMP' 標記（見 RtbCampaignWebService
        // 的 wrapBasic），後端並沒有這個 adType，不能原樣送回去。
        omitBasicProps.push('adType');
      }
      adTypeSetting = ChannelGroupSetting.adTypeSettings().get(
        campaign.basic.adType
      );
    }
    const isOutdoor = adTypeSetting
      ? ChannelGroupSetting.fromId(
          adTypeSetting.channelGroupId
        ).isOutdoorChannel()
      : false;
    let result: any = {
      campaign: _.omitBy(
        {
          ..._.omit(campaign.basic, omitBasicProps),
          adTypeId: campaign.basic.adTypeId,
          tags: campaign.basic.tags.join(','),
          budget: campaign.basic.budget,
          campaignId: campaign.basic.id,
          videoProgressTrackingCode: videoProgressTrackingCode,
          isTestMode: isTestMode(campaign.limitations),
          dealIds: this.getDealIds(campaign.limitations),
          isMonitorEnable: campaign.basic.enableMonitor,
          ageMin: ageMin || RTBCAMPAIGN_DEFAULT_AGE_MIN,
          ageMax: ageMax || RTBCAMPAIGN_DEFAULT_AGE_MAX,
          videoAdViewObjective:
            isOutdoor &&
            [
              L2ObjectOptimizationGoal.VIDEO_VIEWS,
              L2ObjectOptimizationGoal.VIDEO_VIEWS_REACH
            ].includes(campaign.basic.optimize)
              ? {
                  event: VideoAdViewObjective.PROGRESS,
                  offset: 1
                }
              : campaign.basic.videoAdViewObjective,
          isPmp: campaign.basic.isPmp || false
        },
        _.isUndefined
      ),
      limitations: this.wrapLimitationForServer(
        campaign.basic.adType,
        campaign.basic.adTypeId,
        ageMin,
        ageMax,
        ageOptValue,
        campaign.limitations
      )
    };
    return result;
  };

  prepareCreateCampaignPayload(campaign: RtbCampaign) {
    const prepareCreate = _.flow([
      _.cloneDeep,
      this.convertVideoAdEventToEvent,
      (campaign) => this.wrapCampaignForServer(campaign)
    ]);
    return prepareCreate(campaign);
  }

  getDealIds(limitations) {
    const dealIdLimitation = limitations.other
      ? limitations.other.find((limitation) => limitation.type === 'dealId')
      : undefined;
    if (!dealIdLimitation || dealIdLimitation.value.length === 0) {
      return undefined;
    }

    return dealIdLimitation.value.map((selectOption) => selectOption.value);
  }

  showFrequencyControl = (
    l1Object: L1Object,
    optimize: L2ObjectOptimizationGoal
  ) => {
    const { channel, channelGroupId } = l1Object;
    const channelGroupSetting = channelGroupId
      ? ChannelGroupSetting.fromId(channelGroupId)
      : ChannelGroupSetting.fromKey(channel);
    if (channelGroupSetting.isOutdoorChannel()) {
      return false;
    }

    const objective = l1Object.objective;

    return (
      (objective === L1ObjectObjective.AWARENESS ||
        objective === L1ObjectObjective.UNSPECIFIED) &&
      (optimize === L2ObjectOptimizationGoal.REACH ||
        optimize === L2ObjectOptimizationGoal.VIDEO_VIEWS_REACH)
    );
  };

  getAgeLimitationSummary = (limitations) => {
    const ageMinLimitation = limitations.find(
      (limitation) => limitation.type === 'age_min'
    );
    const ageMaxLimitation = limitations.find(
      (limitation) => limitation.type === 'age_max'
    );
    if (ageMinLimitation && ageMaxLimitation) {
      const ageMinOption = ageMinOptions.find(
        (option) => option.value === _.get(ageMinLimitation, 'value')
      );
      const ageMaxOption = ageMaxOptions.find(
        (option) => option.value === _.get(ageMaxLimitation, 'value')
      );
      const ageGroupsHint = getPmax2RtbAgeGroupsByAgeRange(
        _.get(ageMinOption, 'value', 0),
        _.get(ageMaxOption, 'value', 0)
      )
        .map((group) => group.label)
        .join(',');
      return {
        label: i18n.t('limitation.labels.age'),
        value: `${_.get(ageMinOption, 'label', 0)} ~ ${_.get(ageMaxOption, 'label', 0)}`,
        hint: i18n.t('limitation.hints.ageGroups', {
          groups: ageGroupsHint
        })
      };
    }
    return undefined;
  };

  private getDeliveryTargetGroups(
    data: Limitation,
    parentOptions: DeliveryTargetParentOptions | undefined
  ): {
    deliveryTargetGroups?: DeliveryTargetGroup[];
    deliveryTargetUnit?: DeliveryTargetUnit;
    deliveryTargetParentMunicipalities?: Set<string>;
  } {
    const unit = DELIVERY_TARGET_LIMITATION_UNITS[data.type];
    if (!unit || !parentOptions) {
      return {};
    }
    const options = data.value as SelectOptions[];
    const parents =
      parentOptions[data.type] ?? new Map<string, SelectOptions[]>();
    return {
      deliveryTargetGroups: groupDeliveryTargets(options, parents, unit),
      // 一併帶出去：補逐間狀態獨有的門市時要用同一個單位解 value，而通路設定的
      // deliveryUnit 只在活動列有投遞數字時才傳得到，limitation type 才永遠跟著資料。
      deliveryTargetUnit: unit,
      // 只有整個縣市選進來的那些會缺門市，補的範圍限定在它們。
      deliveryTargetParentMunicipalities: parentSelectedMunicipalities(
        options,
        parents
      )
    };
  }

  getLimitationContent = (
    orderType: OrderType,
    agencySegmentName: string,
    deliveryTargetParentOptions: DeliveryTargetParentOptions | undefined,
    limitationData: Limitation[]
  ) => {
    if (!limitationData) {
      return [];
    }

    const normalLimitationSummary = _.compact(
      limitationData.map((data) => {
        const ignoreType = ['unicornlanguage', 'age', 'age_min', 'age_max'];
        if (ignoreType.includes(data.type)) {
          return undefined;
        }

        if (!this.isArray(data.value)) {
          return undefined;
        }

        if (_.defaultTo(data.value, []).length === 0) {
          return undefined;
        }

        if (data.type === 'agencySegment') {
          const segmentValues = data.value.map((value) =>
            getLimitationContentLabel(data.type, value)
          );
          return {
            label: i18n.t('limitation.labels.agencySegment', {
              name: agencySegmentName
            }),
            value: segmentValues.join(', '),
            values: segmentValues
          };
        }

        let labelI18nKey = `limitation.labels.${data.type}`;
        if (!i18n.exists(labelI18nKey)) {
          labelI18nKey = `productGroup.space.attributes.titles.${data.type}`;
        }

        let label = i18n.exists(labelI18nKey)
          ? i18n.t(labelI18nKey)
          : data.type;
        // value 維持逗號串：SummaryDetail（活動／素材／訊息摘要與 L1Object 限制
        // 管理）直接顯示它。明細改成逐筆換行，所以另外附一份沒有 join 的。
        const values = _.uniq(
          data.value.map((value) => getLimitationContentLabel(data.type, value))
        );
        return {
          label,
          value: values.join(', '),
          values,
          // 門市／機台再附一份依縣市分好的兩層結構，只有列表明細會讀。
          ...this.getDeliveryTargetGroups(data, deliveryTargetParentOptions)
        };
      })
    );

    const ageLimitationSummary = this.getAgeLimitationSummary(limitationData);
    if (ageLimitationSummary) {
      return [ageLimitationSummary, ...normalLimitationSummary];
    }
    return normalLimitationSummary;
  };

  getLimitationSummaryData(
    limitationData: LimitationData,
    orderType: OrderType,
    agencySegmentName: string,
    hiddenLimitations: string[] = [],
    deliveryTargetParentOptions?: DeliveryTargetParentOptions
  ) {
    const getLimitationContent = _.partial(
      this.getLimitationContent,
      orderType,
      agencySegmentName,
      deliveryTargetParentOptions
    );
    const getLimitationToShow = (limitations?: Limitation[]) =>
      limitations
        ? limitations.filter(
            (limitation) => !hiddenLimitations.includes(limitation.type)
          )
        : [];
    return _.omitBy(
      {
        include: getLimitationContent(
          getLimitationToShow(limitationData.include)
        ),
        preferred: getLimitationContent(
          getLimitationToShow(limitationData.preferred)
        ),
        nonPreferred: getLimitationContent(
          getLimitationToShow(limitationData.nonPreferred)
        ),
        exclude: getLimitationContent(
          getLimitationToShow(limitationData.exclude)
        ),
        other: limitationData.other
          ? getLimitationContent(getLimitationToShow(limitationData.other))
          : undefined
      },
      _.isEmpty
    );
  }

  checkOptimizeSameAsPriceModel(
    priceModel: RtbCampaignPlanType,
    optimize: L2ObjectOptimizationGoal
  ): boolean {
    return (
      (priceModel === RtbCampaignPlanType.FCPC &&
        (optimize === L2ObjectOptimizationGoal.CLICKS ||
          optimize === L2ObjectOptimizationGoal.SALES)) ||
      (priceModel === RtbCampaignPlanType.FCPM &&
        (optimize === L2ObjectOptimizationGoal.IMPRESSIONS ||
          optimize === L2ObjectOptimizationGoal.REACH)) ||
      (priceModel === RtbCampaignPlanType.FCPV &&
        (optimize === L2ObjectOptimizationGoal.VIDEO_VIEWS ||
          optimize === L2ObjectOptimizationGoal.VIDEO_VIEWS_REACH)) ||
      (priceModel === RtbCampaignPlanType.FVCPM &&
        (optimize === L2ObjectOptimizationGoal.VIDEO_VIEWS ||
          optimize === L2ObjectOptimizationGoal.VIDEO_VIEWS_REACH)) ||
      (priceModel === RtbCampaignPlanType.FACPM &&
        optimize === L2ObjectOptimizationGoal.ACPM) ||
      (priceModel === RtbCampaignPlanType.FWCPM &&
        optimize === L2ObjectOptimizationGoal.WCPM) ||
      (priceModel === RtbCampaignPlanType.FBCPM &&
        optimize === L2ObjectOptimizationGoal.BCPM)
    );
  }

  isSupportVideoAdType(adType: AdType): boolean {
    return [
      AdType.VIDEO,
      AdType.COMBO,
      AdType.THIRD_PARTY,
      AdType.THIRD_PARTY_BOTTOM,
      AdType.THIRD_PARTY_RECTANGLE
    ].includes(adType);
  }

  async getSpaceDeliverabilityData(
    groupId: number,
    adType: string | undefined,
    adTypeId: number | null | undefined,
    isTesting: boolean,
    limitations: LimitationData,
    campaignId?: number | null
  ): Promise<SpaceDeliverabilityData> {
    // RKB-2712: `toServerStructure` 的 type alias 是**雙向**的
    // （`customOutdoorDevice` ⇄ `adspace`，見 LimitationUtil 的 aliasTypeNameData）。
    // 一般 outdoor 活動的 limitation 型別是 `customOutdoorDevice`，需要 alias 成
    // `adspace` 才送得出去；但 PMP 活動的型別本來就是 `adspace`，再過一次 alias
    // 會被**反向**翻成 `customOutdoorDevice`，後端白名單認不出來。
    //
    // 判準必須與**解包端**（`wrapLimitationsFromServer` 在 RtbCampaignWebService
    // 的 `isAdTypeSupportOutdoorTA(json.adType, json.adTypeId)`）是同一個，
    // 否則「包」與「解」不對稱 —— 例如 adTypeId 有值但 adTypeSettings() 查不到
    // （廣告類型被移除／改名）時，解包留在 `adspace`、送出卻翻成
    // `customOutdoorDevice`，會靜默算出整個渠道的門市數。
    // 寫法與同檔的 toFixOutdoorServerLimitations 一致。
    const useAliasName = isAdTypeSupportOutdoorTA(
      adType ?? '',
      adTypeId ?? undefined
    );
    return this.webService.getSpaceDeliverabilityData(
      groupId,
      // PMP 活動沒有 adTypeId，一律送 null（後端收到 null 時視為單螢幕）。
      adTypeId ?? null,
      isTesting,
      toServerStructure(limitations, useAliasName),
      campaignId
    );
  }
}
